import { Component,ChangeDetectorRef } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import jsPDF from 'jspdf';
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ConfirmationService } from 'primeng/api';

// PrimeNG modules
import { DropdownModule } from 'primeng/dropdown';
import { EditorModule } from 'primeng/editor';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { CalendarModule } from 'primeng/calendar';
import { ToastModule } from 'primeng/toast';
import { AutoCompleteModule } from "primeng/autocomplete";
import { RadioButtonModule } from 'primeng/radiobutton';

import { ApiService } from '../../../service/admin/api.service';
import { UserService } from '../../../service/admin/user.service';
import { PrescriptionsService } from '../../../service/admin/prescriptions.service';
import { MedicineService } from '../../../service/admin/medicine.service';
import { ToasterService } from '../../../service/common/toaster.service';

interface ExportMedicineRow {
  key: number;
  medicineName: string;
  potency: string;
  container: string;
  customContainer?: string;
  medicineRef?: any;
}

@Component({
  selector: 'app-certificates',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    DropdownModule,
    EditorModule,
    ButtonModule,
    InputTextModule,
    InputTextareaModule,
    CalendarModule,
    ToastModule,
    AutoCompleteModule,
    RadioButtonModule
  ],
  providers: [ApiService, UserService, PrescriptionsService, MedicineService],
  templateUrl: './certificates.component.html',
  styleUrl: './certificates.component.scss',
})
export class CertificatesComponent {
  // track which tab is currently active so preview updates the correct form
  public activeTab: 'medical' | 'travel' | 'export' | 'hospital' = 'medical';
  readonly certificateCategories: {
    id: 'medical' | 'travel' | 'export';
    label: string;
    description: string;
    icon: string;
  }[] = [
    {
      id: 'medical',
      label: 'Medical',
      description: 'Health & fitness letters',
      icon: 'pi-heart',
    },
    {
      id: 'travel',
      label: 'Travel',
      description: 'Travel & activity clearance',
      icon: 'pi-send',
    },
    {
      id: 'export',
      label: 'Export',
      description: 'Export with medicines',
      icon: 'pi-box',
    },
  ];
  medicalForm!: FormGroup;
  travelForm!: FormGroup;
  exportForm!: FormGroup;
  hospitalForm!: FormGroup;

  public doctorsList: any[] = [];

  medicalTypes = [
    {
      label: 'Medical Examination Certificate',
      value: 'medical examination certificate',
    },
    {
      label: 'General Fitness Certificate',
      value: 'general fitness certificate',
    },
    { label: 'Unfitness Certificate', value: 'unfitness certificate' },
    { label: 'Medical Leave Certificate', value: 'medical leave certificate' },
    {
      label: 'Medical Leave Extension Certificate',
      value: 'medical leave extension certificate',
    },
    {
      label: 'Return to Work / Resume Duty Certificate',
      value: 'return to work certificate',
    },
    {
      label: 'Chronic Illness Certificate',
      value: 'chronic illness certificate',
    }];

  travelTypes = [
    {
      label: 'Fitness for Travel Certificate',
      value: 'fitness for travel certificate',
    },
    {
      label: 'Sports / Adventure Fitness Certificate',
      value: 'sports adventure fitness certificate',
    }];

  exportTypes = [
    {
      label: 'Export Certificate',
      value: 'export certificate',
    }];

  specialTypes = [
    { label: 'Pregnancy Certificate', value: 'pregnancy certificate' },
    { label: 'Vaccination Certificate', value: 'vaccination certificate' },
    {
      label: 'Injury / Accident Certificate',
      value: 'injury accident certificate',
    }];

  hospitalTypes = [
    { label: 'Discharge Certificate', value: 'discharge certificate' }];

  templates: Record<string, string> = {
    'medical examination certificate': `
  This is to certify that the individual was examined on the above-mentioned date. Based on clinical evaluation, the general health status of the individual has been assessed. This certificate is issued upon request for official purposes such as employment, education, or insurance.`,

    'general fitness certificate': `
  This is to certify that the individual has been medically examined and is found to be physically fit, with no medical contraindications at the time of examination, to perform routine professional or daily activities.`,

    'unfitness certificate': `
  This is to certify that the individual was examined and is presently unfit to perform routine work or academic activities due to medical reasons. Rest and medical care have been advised as deemed necessary.`,

    'medical leave certificate': `
  This is to certify that the individual was under medical care and was advised rest due to illness. The individual was medically unfit to attend work or school during the advised period.`,

    'medical leave extension certificate': `
  This is to certify that upon re-evaluation, the individual continues to require medical rest. The medical leave previously granted has been extended based on current clinical findings.`,

    'return to work certificate': `
  This is to certify that the individual has recovered satisfactorily and is medically fit to resume work or academic activities from the specified date.`,

    'chronic illness certificate': `
  This is to certify that the individual is diagnosed with a chronic medical condition and is under ongoing medical care. This certificate is issued for official purposes requiring medical confirmation.`,

    'fitness for travel certificate': `
  This is to certify that the individual has been medically examined and is found fit to undertake travel by the specified mode, with no current medical condition likely to compromise safety during travel.`,

    'sports adventure fitness certificate': `
  This is to certify that the individual has undergone a medical examination and is deemed fit for participation in sports or adventure activities. Issued based on clinical examination only. No responsibility is accepted for injury during activity.`,

    'export certificate': `
  This is to certify that the individual has been medically examined on the above-mentioned date. Based on clinical evaluation, the individual is found to be medically fit and free from any communicable disease or condition that would contraindicate export-related requirements. This Export Certificate is issued upon request for official, overseas, or export documentation purposes.`,
  };

  previewData: any = null;

  logo = '/logo.png'; // <-- for local
  // logo = '/clinic/logo.png'; // <-- for server

  private apiUrl: string | undefined;
  public patientsList: any[] = [];

  public selectedPatient: any = null;
  public exportMedicines: ExportMedicineRow[] = [];
  public filteredMedicineSuggestions: any[] = [];
  public medicineMasterList: any[] = [];
  public loadingExportMedicines = false;
  private exportMedicineKey = 0;

  readonly containerOptions = [
    { label: 'Globules 10 gm', value: 'globules_10g' },
    { label: 'Globules 20 gm', value: 'globules_20g' },
    { label: 'Globules 30 gm', value: 'globules_30g' },
    { label: 'Globules 40 gm', value: 'globules_40g' },
    { label: 'Pilules', value: 'pilules' },
    { label: 'Liquid 1 dram', value: 'liquid_1_dram' },
    { label: 'Liquid 2 dram', value: 'liquid_2_dram' },
    { label: 'Liquid 4 dram', value: 'liquid_4_dram' },
    { label: 'Liquid 1 oz', value: 'liquid_1_oz' },
    { label: 'Dropper bottle', value: 'dropper_bottle' },
    { label: 'Mother tincture bottle', value: 'mt_bottle' },
    { label: 'Other', value: 'other' },
  ];

  private readonly containerCodeLabels: Record<string, string> = {
    '101': 'globules_10g',
    '102': 'globules_20g',
    '103': 'globules_30g',
    '104': 'globules_40g',
    '201': 'liquid_1_dram',
    '202': 'liquid_2_dram',
    '203': 'liquid_4_dram',
    '204': 'liquid_1_oz',
  };

  constructor(
    private fb: FormBuilder, 
    private http: HttpClient, 
    private apiService: ApiService,
    private userService: UserService,
    private prescriptionsService: PrescriptionsService,
    private medicineService: MedicineService,
    private toaster: ToasterService, // private confirmationService: ConfirmationService,
    private cdr: ChangeDetectorRef
  ) {
    this.apiUrl = this.apiService.getBaseUrl();
   }

  ngOnInit(): void {
    // Initialize forms
    this.medicalForm = this.fb.group({
      patientName: ['', Validators.required],
      age: ['', [Validators.required, Validators.min(0)]],
      gender: ['', Validators.required],
      date: [new Date(), Validators.required],
      doctorName: ['Dr. Subodh Bhade', Validators.required],
      remarks: [''],
      certificateType: ['medical examination certificate', Validators.required],
      certificateBody: [this.templates['medical examination certificate']],
      paperSize: ['a5']
    });

    this.travelForm = this.fb.group({
      patientName: ['', Validators.required],
      age: ['', [Validators.required, Validators.min(0)]],
      gender: ['', Validators.required],
      date: [new Date(), Validators.required],
      doctorName: ['Dr. Subodh Bhade', Validators.required],
      remarks: [''],
      certificateType: ['fitness for travel certificate', Validators.required],
      certificateBody: [this.templates['fitness for travel certificate']],
      paperSize: ['a5']
    });

    this.exportForm = this.fb.group({
      patientName: ['', Validators.required],
      age: ['', [Validators.required, Validators.min(0)]],
      gender: ['', Validators.required],
      date: [new Date(), Validators.required],
      doctorName: ['Dr. Subodh Bhade', Validators.required],
      remarks: [''],
      certificateType: ['export certificate', Validators.required],
      certificateBody: [this.templates['export certificate']],
      paperSize: ['a5']
    });

    this.hospitalForm = this.fb.group({
      patientName: ['', Validators.required],
      age: ['', [Validators.required, Validators.min(0)]],
      gender: ['', Validators.required],
      date: [new Date(), Validators.required],
      doctorName: ['Dr. Subodh Bhade', Validators.required],
      remarks: [''],
      certificateType: ['discharge certificate', Validators.required],
      certificateBody: [this.templates['discharge certificate']],
      paperSize: ['a5']
    });

    // Live preview
    this.medicalForm.valueChanges.subscribe(() => {
      this.updatePreview('medical');
    });

    this.travelForm.valueChanges.subscribe(() => {
      this.updatePreview('travel');
    });

    this.exportForm.valueChanges.subscribe(() => {
      this.updatePreview('export');
    });

    this.hospitalForm.valueChanges.subscribe(() => {
      this.updatePreview('hospital');
    });

    this.getDoctors();
    this.loadMedicineMaster();
    this.resetExportMedicines();
  }

  private loadMedicineMaster(): void {
    this.medicineService.getMedicine().subscribe({
      next: (res: any) => {
        this.medicineMasterList = Array.isArray(res?.data) ? res.data : [];
      },
      error: () => {
        this.medicineMasterList = [];
      },
    });
  }

  private nextExportMedicineKey(): number {
    this.exportMedicineKey += 1;
    return this.exportMedicineKey;
  }

  resetExportMedicines(): void {
    this.exportMedicines = [this.createExportMedicineRow()];
  }

  private createExportMedicineRow(partial?: Partial<ExportMedicineRow>): ExportMedicineRow {
    return {
      key: this.nextExportMedicineKey(),
      medicineName: partial?.medicineName || '',
      potency: partial?.potency || '',
      container: partial?.container || '',
      customContainer: partial?.customContainer || '',
      medicineRef: partial?.medicineRef,
    };
  }

  addExportMedicineRow(): void {
    this.exportMedicines = [...this.exportMedicines, this.createExportMedicineRow()];
    this.onExportMedicineChange();
  }

  removeExportMedicineRow(index: number): void {
    if (this.exportMedicines.length <= 1) {
      this.resetExportMedicines();
      this.onExportMedicineChange();
      return;
    }
    this.exportMedicines = this.exportMedicines.filter((_, i) => i !== index);
    this.onExportMedicineChange();
  }

  searchMedicines(event: any): void {
    const query = String(event?.query || '').trim().toLowerCase();
    const list = this.medicineMasterList || [];
    this.filteredMedicineSuggestions = list
      .filter((medicine) => {
        if (!query) {
          return true;
        }
        const haystack = [medicine?.name, medicine?.potency, medicine?.code]
          .map((value) => String(value ?? '').toLowerCase())
          .join(' ');
        return haystack.includes(query);
      })
      .slice(0, 25)
      .map((medicine) => ({
        ...medicine,
        label: this.formatMedicineOption(medicine),
      }));
  }

  formatMedicineOption(medicine: any): string {
    const name = String(medicine?.name || '').trim();
    const potency = String(medicine?.potency || '').trim();
    const code = String(medicine?.code || '').trim();
    const parts = [name, potency].filter(Boolean);
    const base = parts.join(' — ');
    return code ? `${base} (${code})` : base;
  }

  onExportMedicineSelect(row: ExportMedicineRow, event: any): void {
    const medicine = event?.value ?? event;
    if (!medicine || typeof medicine === 'string') {
      return;
    }
    row.medicineRef = medicine;
    row.medicineName = medicine.name || '';
    row.potency = medicine.potency || '';
    this.onExportMedicineChange();
  }

  onExportMedicineChange(): void {
    if (this.activeTab === 'export') {
      this.updatePreview('export');
    }
  }

  getContainerLabel(row: ExportMedicineRow | string): string {
    if (typeof row === 'string') {
      return this.containerOptions.find((option) => option.value === row)?.label || row || '—';
    }
    if (row.container === 'other') {
      return row.customContainer?.trim() || 'Other';
    }
    return this.containerOptions.find((option) => option.value === row.container)?.label || row.container || '—';
  }

  get exportMedicinesForPreview(): ExportMedicineRow[] {
    return (this.exportMedicines || []).filter(
      (row) =>
        row.medicineName?.trim() ||
        row.potency?.trim() ||
        row.container?.trim() ||
        row.customContainer?.trim(),
    );
  }

  loadExportMedicinesFromPrescription(silent = false): void {
    const patientId = this.selectedPatient?.id ?? this.selectedPatient?.patient_id;
    if (!patientId) {
      if (!silent) {
        this.toaster.warn('Select a patient first.', 'No patient');
      }
      return;
    }

    this.loadingExportMedicines = true;
    this.prescriptionsService.getLatestPrescription(Number(patientId)).subscribe({
      next: (res: any) => {
        const medicinesText = res?.data?.medicines || res?.medicines || '';
        const rows = this.parsePrescriptionMedicines(medicinesText);
        this.exportMedicines = rows.length ? rows : [this.createExportMedicineRow()];
        this.loadingExportMedicines = false;
        this.onExportMedicineChange();
        if (!silent) {
          if (!rows.length) {
            this.toaster.info('No prescribed medicines found in the latest prescription.', 'Info');
          } else {
            this.toaster.success(`${rows.length} medicine(s) loaded from prescription.`, 'Loaded');
          }
        }
      },
      error: () => {
        this.loadingExportMedicines = false;
        if (!silent) {
          this.toaster.error('Could not load medicines from prescription.', 'Error');
        }
      },
    });
  }

  private parsePrescriptionMedicines(value: string): ExportMedicineRow[] {
    const body = this.stripMedicinesDatePrefix(value);
    if (!body.trim()) {
      return [];
    }

    return body
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => this.parsePrescriptionMedicineLine(line));
  }

  private stripMedicinesDatePrefix(value: string): string {
    const text = String(value || '').trim();
    const datedPrefix = text.match(/^\d{2}\/\d{2}\/\d{4}\s*-\s*/);
    if (datedPrefix) {
      return text.slice(datedPrefix[0].length).trim();
    }
    return text;
  }

  private parsePrescriptionMedicineLine(line: string): ExportMedicineRow {
    const shorthand = line.match(/^(\d+)-([^/]+)\/(\d+)/);
    if (!shorthand) {
      return this.createExportMedicineRow({ medicineName: line });
    }

    const [, code, potency, containerCode] = shorthand;
    const master = this.medicineMasterList.find(
      (medicine) => String(medicine?.code || '').trim() === String(code).trim(),
    );

    return this.createExportMedicineRow({
      medicineName: master?.name || `Medicine ${code}`,
      potency: master?.potency || potency?.trim() || '',
      container: this.containerCodeLabels[containerCode] || '',
      medicineRef: master,
    });
  }

  private buildPreviewPayload(formValue: any, tab: 'medical' | 'travel' | 'export' | 'hospital') {
    return {
      ...formValue,
      dateFormatted: formValue.date
        ? new Date(formValue.date).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
        : '',
      exportMedicines: tab === 'export' ? this.exportMedicinesForPreview : [],
    };
  }

  getDoctors() {
    this.userService.getDoctors().subscribe({
      next: (res: any) => {
        this.doctorsList = Array.isArray(res.data)
          ? res.data.map((doctor:any) => ({
              label: doctor.name,
              value: doctor.id
            }))
          : [];
      },
      error: (err) => {
        console.error('Doctor API error:', err);
        this.doctorsList = [];
      }
    });
  }

  searchPatients(event: any) {
    const query = (event?.query || '').trim();
    if (query.length < 2) {
      this.patientsList = [];
      return;
    }

    this.http.get<any>(this.apiUrl + '/patient/search', { params: { q: query }, ...this.getAuthHeaders() })
      .subscribe(res => {
        if (!res.data || res.data.length === 0) {
          this.patientsList = [];
        } else {
          this.patientsList = res.data.map((p: any) => {
            const displayName = [p.salutation, p.full_name].filter(Boolean).join(' ').trim();
            return {
              ...p,
              name: displayName,
              search_label: `${displayName} (${p.patient_number || '—'})`,
            };
          });
        }
        this.cdr.detectChanges();
      }, error => {
        this.patientsList = [];
        this.toaster.info('No patients found.', 'Info');
        this.cdr.detectChanges();
      });
  }

  getPatientInitials(name: string | null | undefined): string {
    const parts = (name || '')
      .replace(/^(Mr\.|Mrs\.|Ms\.|Miss|Shri\.|Smt\.|Ku\.)\s+/i, '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!parts.length) {
      return '?';
    }
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  }

  // onPatientSelect(event: any) {
  //   this.selectedPatient = event.value;
  // }

  onPatientSelect(event: any) {
    const patient = event.value;
    this.selectedPatient = patient;
    if (!patient) return;

    const fullName =
      patient.name ||
      [patient.salutation, patient.full_name].filter(Boolean).join(' ').trim();

    // compute age from DOB if available, fallback to provided age fields
    let age: number | string = '';
    if (patient.date_of_birth) {
      const dob = new Date(patient.date_of_birth);
      let years = new Date().getFullYear() - dob.getFullYear();
      const m = new Date().getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && new Date().getDate() < dob.getDate())) years--;
      age = years;
    } else if (patient.age_years ?? patient.age) {
      age = patient.age_years ?? patient.age;
    }

    // normalize gender (depends on API: 'gender' or 'sex')
    let gender = patient.gender ?? patient.sex ?? '';
    
    // Determine gender based on salutation if not already set
    if (!gender && patient.salutation) {
      const salutation = patient.salutation.trim();
      if (salutation === 'Shri.' || salutation === 'Mr.' || salutation === 'Ku.') {
        gender = 'Male';
      } else if (salutation === 'Mrs.' || salutation === 'Ms.' || salutation === 'Miss' || salutation === 'Smt.') {
        gender = 'Female';
      }
    }

    // patch medical form (control names: patientName, age, gender)
    this.medicalForm.patchValue({
      patientName: fullName,
      age,
      gender
    });

    this.travelForm.patchValue({
      patientName: fullName,
      age,
      gender
    });

    this.exportForm.patchValue({
      patientName: fullName,
      age,
      gender
    });

    this.loadExportMedicinesFromPrescription(true);

    // update live preview for the active tab
    this.updatePreview(this.activeTab);
  }

  onTabChange(event: any) {
    const idx = event?.index ?? 0;
    this.activeTab = idx === 0 ? 'medical' : idx === 1 ? 'travel' : 'export';
    this.updatePreview(this.activeTab);
  }

  setCertificateCategory(tab: 'medical' | 'travel' | 'export'): void {
    if (this.activeTab === tab) {
      return;
    }
    this.activeTab = tab;
    this.updatePreview(tab);
  }

  get activeCategoryMeta() {
    return (
      this.certificateCategories.find((c) => c.id === this.activeTab) ||
      this.certificateCategories[0]
    );
  }

  get selectedPatientLabel(): string {
    const fromSelection =
      this.selectedPatient?.name ||
      this.selectedPatient?.full_name ||
      this.selectedPatient?.search_label ||
      '';
    if (fromSelection) {
      return String(fromSelection).trim();
    }
    const form =
      this.activeTab === 'travel'
        ? this.travelForm
        : this.activeTab === 'export'
          ? this.exportForm
          : this.medicalForm;
    return String(form?.get('patientName')?.value || '').trim();
  }

  get selectedPatientMeta(): string {
    const form =
      this.activeTab === 'travel'
        ? this.travelForm
        : this.activeTab === 'export'
          ? this.exportForm
          : this.medicalForm;
    const age = form?.get('age')?.value;
    const gender = form?.get('gender')?.value;
    const parts = [age ? `Age ${age}` : '', gender || ''].filter(Boolean);
    return parts.join(' · ');
  }

  clearSelectedPatient(): void {
    this.selectedPatient = null;
    for (const form of [this.medicalForm, this.travelForm, this.exportForm]) {
      form?.patchValue({ patientName: '', age: '', gender: '' });
    }
    this.resetExportMedicines();
    this.updatePreview(this.activeTab === 'hospital' ? 'medical' : this.activeTab);
  }

  generateActivePdf(): void {
    const tab = this.activeTab === 'hospital' ? 'medical' : this.activeTab;
    this.generatePDFlocally(tab);
  }

  // Quill / p-editor emits text-change events; sync editor HTML to form control
  onEditorTextChange(event: any, tab: 'medical' | 'travel' | 'export' | 'hospital') {
    // event payload varies; try common properties in order
      const html = event?.html ?? event?.htmlValue ?? event?.editor?.root?.innerHTML ?? '';
      const form = tab === 'medical'
        ? this.medicalForm
        : tab === 'travel'
          ? this.travelForm
          : tab === 'export'
            ? this.exportForm
            : this.hospitalForm;
      if (!form) return;

      // Avoid writing back into the form control (this causes Quill to reset cursor to start).
      // Instead update the live preview directly using the editor HTML.
      const f = { ...form.value, certificateBody: html };
      this.previewData = this.buildPreviewPayload(
        { ...f, certificateBody: html },
        tab,
      );
      this.cdr.detectChanges();
  }

  updatePreview(tab: 'medical' | 'travel' | 'export' | 'hospital') {
    let form: FormGroup;

    if (tab === 'medical') form = this.medicalForm;
    else if (tab === 'travel') form = this.travelForm;
    else if (tab === 'export') form = this.exportForm;
    else form = this.hospitalForm;

    this.previewData = this.buildPreviewPayload(form.value, tab);
  }

  // Update template when type changes
  onTypeChange(tab: 'medical' | 'travel' | 'export' | 'hospital') {
    let form: FormGroup;

    if (tab === 'medical') form = this.medicalForm;
    else if (tab === 'travel') form = this.travelForm;
    else if (tab === 'export') form = this.exportForm;
    else form = this.hospitalForm;

    const type = form.get('certificateType')?.value;
    form.get('certificateBody')?.setValue(this.templates[type] || '');

    this.updatePreview(tab);
  }

  // Generate PDF
  generatePDFlocally(tab: 'medical' | 'travel' | 'export' | 'hospital') {
    let form: FormGroup;
    if (tab === 'medical') form = this.medicalForm;
    else if (tab === 'travel') form = this.travelForm;
    else if (tab === 'export') form = this.exportForm;
    else form = this.hospitalForm;  

    const f = {
      ...form.value,
      exportMedicines: tab === 'export' ? this.exportMedicinesForPreview : [],
    };

    //! ======================================================================== A4 ======================
    // const doc = new jsPDF({ unit: 'pt', format: 'a4' });

    //! ======================================================================== A5 ======================
    
    // Check paper size from form
    const paperSize = f.paperSize || 'a5';
    
    // Default to A5 config
    let format = 'a5';
    let orientation: 'p' | 'l' = 'p';   

    if (paperSize === 'a4') {
        format = 'a4';
    }

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: format,
    });

    // Add logo
    const img = new Image();
    img.src = '/logo.png'; //! <--- For local
    // img.src = '/clinic/logo.png'; //! <--- For server

    if (paperSize === 'a4') {
        img.onload = () => this.buildPDFA4(doc, f, img);
        img.onerror = () => this.buildPDFA4(doc, f);
    } else {
        img.onload = () => this.buildPDFA5(doc, f, img);
        img.onerror = () => this.buildPDFA5(doc, f);
    }
  }

  //! ======================================================================== A4 ======================
  private buildPDFA4(doc: jsPDF, f: any, img?: HTMLImageElement) {

    /* ================= PAGE CONFIG (A4) ================= */

    const pageWidth = doc.internal.pageSize.getWidth();   // 210mm
    const pageHeight = doc.internal.pageSize.getHeight(); // 297mm

    const marginX = 20;
    let y = 20;

    const contentWidth = pageWidth - marginX * 2;

    const darkGray: [number, number, number] = [51, 51, 51];

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkGray);

    /* ================= HEADER ================= */

    const col1X = marginX;
    // Align contact info to the right
    const col2X = pageWidth - marginX - 60; 

    doc.setFont('times', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(0, 0, 0);
    doc.text('DR. SUBODH BHADE', col1X, y + 5);

    y += 12;
    doc.setFontSize(10);
    doc.text('B.H.M.S. MD (HMM), CAc, ND.', col1X, y);

    y += 6;
    doc.text('HOMOEOPATHIC CONSULTANT', col1X, y);

    y += 6;
    doc.setFont('times', 'normal');
    doc.setFontSize(10);
    doc.text('Regd. No. 23009', col1X, y);

    /* ===== CONTACT INFO (RIGHT) ===== */

    let ry = 25; 
    const lineHeight = 5;

    doc.setFont('times', 'bold');
    doc.setFontSize(9);
    
    const addContactRow = (label: string, value: string) => {
        doc.setFont('times', 'bold');
        doc.text(label, col2X, ry);
        doc.setFont('times', 'normal');
        doc.text(value, col2X + 22, ry); 
        ry += lineHeight;
    };

    addContactRow('Mukundnagar:', '020-24261180 / 24266888');
    addContactRow('Rasta Peth:', '020-26126844 / 26132613');
    addContactRow('Kothrud:', '020-25387888 / 25387886');
    ry += 2;
    addContactRow('Email:', 'drsubodh@hotmail.com');
    addContactRow('Mobile:', '98230 14580');

    /* ===== SEPARATOR ===== */

    y = Math.max(y + 10, ry + 5);

    doc.setLineWidth(0.5);
    doc.line(marginX, y, pageWidth - marginX, y);

    y += 15;

    /* ================= PATIENT INFO ================= */

    const fieldY = y;
    const colPatientX = marginX;
    const colAgeX = marginX + 110; 

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Patient Name:', colPatientX, fieldY);
    doc.setFont('helvetica', 'normal');
    doc.text(f.patientName || '', colPatientX + 30, fieldY);

    doc.setFont('helvetica', 'bold');
    doc.text('Age:', colAgeX, fieldY);
    doc.setFont('helvetica', 'normal');
    doc.text(`${f.age || ''} Years`, colAgeX + 10, fieldY);

    y += 8;

    doc.setFont('helvetica', 'bold');
    doc.text('Date:', colPatientX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(
      f.date
        ? new Date(f.date).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })
        : '',
        colPatientX + 30,
      y
    );

    doc.setFont('helvetica', 'bold');
    doc.text('Sex:', colAgeX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(f.gender || '', colAgeX + 10, y);

    y += 20;

    /* ================= CERTIFICATE TITLE ================= */

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    const title = (f.certificateType || 'Medical Certificate').toUpperCase();
    doc.text(title, pageWidth / 2, y, { align: 'center' });

    y += 15;

    /* ================= CERTIFICATE BODY ================= */
    
    // Reserve footer space: Signature ~30mm
    const footerHeight = 40;
    const maxContentY = pageHeight - footerHeight - marginX;

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = f.certificateBody || '';
    const bodyText = tempDiv.textContent?.replace(/\s+/g, ' ').trim() || '';

    if (bodyText) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);

      const splitText = doc.splitTextToSize(bodyText, contentWidth);
      const lineHeightBody = 6; // mm

      splitText.forEach((line: string, i: number) => {
          // New page check for A4
          if (y > maxContentY) {
              doc.addPage();
              y = 20; 
          }
          doc.text(line, marginX, y);
          y += lineHeightBody;
      });

      y += 10;
    }

    y = this.appendExportMedicinesToPdf(
      doc,
      f.exportMedicines || [],
      y,
      marginX,
      contentWidth,
      maxContentY,
      pageHeight,
      12,
      6,
    );

    /* ================= REMARKS ================= */

    if (f.remarks) {
      // Check space before starting remarks
      if (y > maxContentY - 20) {
          doc.addPage();
          y = 20;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('Remarks:', marginX, y);

      y += 6;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);

      const remarksText = f.remarks.replace(/\s+/g, ' ').trim();
      const splitRemarks = doc.splitTextToSize(remarksText, contentWidth);
      const lineHeightRem = 6;

      splitRemarks.forEach((line: string, i: number) => {
        if (y > maxContentY) {
             doc.addPage();
             y = 20;
        }
        doc.text(line, marginX, y);
        y += lineHeightRem;
      });

      y += 10;
    }

    /* ================= SIGNATURE ================= */

    let footerY = pageHeight - 35;
    
    // Ensure footer doesn't overlap if content is strangely positioned, 
    // though the page break logic above handles most cases.
    // If we just added a page, footerY is fine at bottom.
    // If y is close to footerY, push footer or add page?
    // In A4, we usually want footer at bottom.
    
    if (y > footerY - 10) {
        doc.addPage();
        footerY = pageHeight - 35;
    }

    const sigX = pageWidth - marginX - 70;

    doc.setLineWidth(0.6);
    doc.setDrawColor(...darkGray);
    doc.line(sigX, footerY, pageWidth - marginX, footerY);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.text(f.doctorName || 'Dr. Subodh Bhade', sigX, footerY + 6);

    doc.setFontSize(9);
    doc.text('B.H.M.S. MD (HMM), CAc, ND.', sigX, footerY + 11);

    /* ================= SAVE ================= */

    const slug = (s: string) =>
      s
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');

    const fileName = `${slug(f.certificateType || 'certificate')}_${slug(
      f.patientName || 'patient'
    )}_A4.pdf`;

    doc.save(fileName);
  }

  //! ======================================================================== A5 ======================
  private buildPDFA5(doc: jsPDF, f: any, img?: HTMLImageElement) {

    /* ================= PAGE CONFIG (A5) ================= */

    const pageWidth = doc.internal.pageSize.getWidth();   // 148mm
    const pageHeight = doc.internal.pageSize.getHeight(); // 210mm

    const marginX = 10; // Slightly reduced margin for more space
    let y = 10;

    const contentWidth = pageWidth - marginX * 2;

    const darkGray: [number, number, number] = [51, 51, 51];

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkGray);

    /* ================= HEADER ================= */

    const col1X = marginX;
    // Align contact info to the right
    const col2X = pageWidth - marginX - 55; // Approx 55mm wide for contact info

    doc.setFont('times', 'bold');
    doc.setFontSize(15); // Slightly smaller
    doc.setTextColor(0, 0, 0);
    doc.text('DR. SUBODH BHADE', col1X, y + 4);

    y += 10;
    doc.setFontSize(8);
    doc.text('B.H.M.S. MD (HMM), CAc, ND.', col1X, y);

    y += 4;
    doc.text('HOMOEOPATHIC CONSULTANT', col1X, y);

    y += 4;
    doc.setFont('times', 'normal');
    doc.setFontSize(8);
    doc.text('Regd. No. 23009', col1X, y);

    /* ===== CONTACT INFO (RIGHT) ===== */

    let ry = 14; 
    const lineHeight = 3.5;

    doc.setFont('times', 'bold');
    doc.setFontSize(7);
    
    // Helper for rows
    const addContactRow = (label: string, value: string) => {
        doc.setFont('times', 'bold');
        doc.text(label, col2X, ry);
        doc.setFont('times', 'normal');
        doc.text(value, col2X + 18, ry); // Align value
        ry += lineHeight;
    };

    addContactRow('Mukundnagar:', '020-24261180 / 24266888');
    addContactRow('Rasta Peth:', '020-26126844 / 26132613');
    addContactRow('Kothrud:', '020-25387888 / 25387886');
    ry += 1;
    addContactRow('Email:', 'drsubodh@hotmail.com');
    addContactRow('Mobile:', '98230 14580');

    /* ===== SEPARATOR ===== */

    y = Math.max(y + 5, ry + 2);

    doc.setLineWidth(0.4);
    doc.line(marginX, y, pageWidth - marginX, y);

    y += 8;

    /* ================= PATIENT INFO ================= */

    // Use a grid with explicit widths
    const fieldY = y;
    const colPatientX = marginX;
    const colAgeX = marginX + 85; 

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Patient Name:', colPatientX, fieldY);
    doc.setFont('helvetica', 'normal');
    // Truncate long names if needed or let flow? A5 width is small.
    // Let's assume name fits or wraps slightly. 
    // We'll give it roughly 60mm
    doc.text(f.patientName || '', colPatientX + 22, fieldY);

    doc.setFont('helvetica', 'bold');
    doc.text('Age:', colAgeX, fieldY);
    doc.setFont('helvetica', 'normal');
    doc.text(`${f.age || ''} yrs`, colAgeX + 8, fieldY);

    y += 6;

    doc.setFont('helvetica', 'bold');
    doc.text('Date:', colPatientX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(
      f.date
        ? new Date(f.date).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
        : '',
        colPatientX + 22,
      y
    );

    doc.setFont('helvetica', 'bold');
    doc.text('Sex:', colAgeX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(f.gender || '', colAgeX + 8, y);

    y += 15;

    /* ================= CERTIFICATE TITLE ================= */

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    const title = (f.certificateType || 'Medical Certificate').toUpperCase();
    doc.text(title, pageWidth / 2, y, { align: 'center' });

    y += 10;

    /* ================= CERTIFICATE BODY ================= */
    
    // We need to reserve space for footer.
    // Footer height approx: 25mm (signature) + 5mm (padding) = 30mm
    const footerHeight = 35;
    const maxContentY = pageHeight - footerHeight - marginX; // Bottom limit for content

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = f.certificateBody || '';
    const bodyText = tempDiv.textContent?.replace(/\s+/g, ' ').trim() || '';

    if (bodyText) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);

      // Simple text splitting
      const splitText = doc.splitTextToSize(bodyText, contentWidth);
      
      const lineHeightBody = 5; // mm

      // Check if it fits
      const neededHeight = splitText.length * lineHeightBody;
      
      if (y + neededHeight > maxContentY) {
          // Warning: Content might be too long. 
          // For a certificate, splitting pages is ugly. 
          // We'll try to squeeze or just print it and footer might overlap or push.
          // Option: Reduce font size slightly if too long?
          // Let's just print.
      }

      splitText.forEach((line: string, i: number) => {
        // Stop if we hit the footer zone? 
        // No, let's print, but user should know A5 is small.
        doc.text(line, marginX, y + i * lineHeightBody);
      });

      y += splitText.length * lineHeightBody + 6;
    }

    y = this.appendExportMedicinesToPdf(
      doc,
      f.exportMedicines || [],
      y,
      marginX,
      contentWidth,
      maxContentY,
      pageHeight,
      10,
      5,
    );

    /* ================= REMARKS ================= */

    if (f.remarks) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('Remarks:', marginX, y);

      y += 5;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);

      const remarksText = f.remarks.replace(/\s+/g, ' ').trim();
      const splitRemarks = doc.splitTextToSize(remarksText, contentWidth);
      const lineHeightRem = 4.5;

      splitRemarks.forEach((line: string, i: number) => {
        doc.text(line, marginX, y + i * lineHeightRem);
      });

      y += splitRemarks.length * lineHeightRem + 6;
    }

    /* ================= SIGNATURE ================= */

    // Footer should be at bottom OR at least below content with some gap
    let footerY = pageHeight - 25;
    
    // If content pushed y further down, push footer down (which might clip) 
    // or just let it be (risk overlap).
    // Better strategy: Ensure footerY is at least y + 10
    if (footerY < y + 10) {
        footerY = y + 10; // This might go off page, but better than overlap
    }

    // However, for A5 certificate, we usually want it fixed at bottom if space permits.
    // If we pushed it off page, we should ideally add a page.
    if (footerY > pageHeight - 15) {
        doc.addPage();
        footerY = 20;
    }

    const sigX = pageWidth - marginX - 60;

    doc.setLineWidth(0.6);
    doc.setDrawColor(...darkGray);
    doc.line(sigX, footerY, pageWidth - marginX, footerY);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(f.doctorName || 'Dr. Subodh Bhade', sigX, footerY + 5);

    doc.setFontSize(7);
    doc.text('B.H.M.S. MD (HMM), CAc, ND.', sigX, footerY + 9);

    /* ================= SAVE ================= */

    const slug = (s: string) =>
      s
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');

    const fileName = `${slug(f.certificateType || 'certificate')}_${slug(
      f.patientName || 'patient'
    )}.pdf`;

    doc.save(fileName);
  }


  // Generate Laravel PDF
  generatePDF(tab: 'medical' | 'travel' | 'export' | 'hospital') {

    let form: FormGroup;

    if (tab === 'medical') {
      form = this.medicalForm;
    } else if (tab === 'travel') {
      form = this.travelForm;
    } else if (tab === 'export') {
      form = this.exportForm;
    } else {
      form = this.hospitalForm;
    }

    const f = form.value;

    // prepare payload for Laravel
    const payload = {
      patientName: f.patientName,
      age: f.age,
      gender: f.gender,
      date: f.date,
      doctorName: f.doctorName,
      certificateType: f.certificateType,
      certificateBody: f.certificateBody,
      remarks: f.remarks
    };

    this.apiService.generateCertificate(payload)
      .subscribe((blob: any) => {

        const fileURL = URL.createObjectURL(blob);
        window.open(fileURL);

        this.toaster.success('Certificate PDF generated and opened.');

      }, (error: any) => {

        this.toaster.error('Failed to generate certificate PDF.');

      });
  }


  private getAuthHeaders() {
    const token = localStorage.getItem('jwtToken');
    return {
      headers: new HttpHeaders({
        'Authorization': `Bearer ${token}`
      })
    }
  }

  private appendExportMedicinesToPdf(
    doc: jsPDF,
    medicines: ExportMedicineRow[],
    startY: number,
    marginX: number,
    contentWidth: number,
    maxContentY: number,
    pageHeight: number,
    fontSize: number,
    lineHeight: number,
  ): number {
    if (!medicines?.length) {
      return startY;
    }

    let y = startY;

    if (y > maxContentY - 20) {
      doc.addPage();
      y = 20;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(fontSize);
    doc.text('Prescribed Medicines:', marginX, y);
    y += lineHeight + 1;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(fontSize - 1);
    doc.text('Medicine', marginX, y);
    doc.text('Potency', marginX + contentWidth * 0.52, y);
    doc.text('Container', marginX + contentWidth * 0.72, y);
    y += lineHeight;

    doc.setLineWidth(0.2);
    doc.line(marginX, y - 1.5, marginX + contentWidth, y - 1.5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(fontSize - 1);

    medicines.forEach((row) => {
      if (y > maxContentY) {
        doc.addPage();
        y = 20;
      }

      const medicineLines = doc.splitTextToSize(row.medicineName || '—', contentWidth * 0.48);
      const potencyLines = doc.splitTextToSize(row.potency || '—', contentWidth * 0.18);
      const containerLines = doc.splitTextToSize(this.getContainerLabel(row), contentWidth * 0.26);
      const rowLines = Math.max(medicineLines.length, potencyLines.length, containerLines.length);

      for (let i = 0; i < rowLines; i++) {
        if (y > maxContentY) {
          doc.addPage();
          y = 20;
        }
        if (medicineLines[i]) {
          doc.text(medicineLines[i], marginX, y);
        }
        if (potencyLines[i]) {
          doc.text(potencyLines[i], marginX + contentWidth * 0.52, y);
        }
        if (containerLines[i]) {
          doc.text(containerLines[i], marginX + contentWidth * 0.72, y);
        }
        y += lineHeight;
      }

      y += 1;
    });

    return y + 4;
  }
}
